const DISPLAY_REMAINING_LIMIT = 12;
const DISPLAY_DONE_LIMIT = 8;

export function normalizeProgressPath(path) {
  return String(path ?? "")
    .replace(/^\/+/, "")
    .trim();
}

function toPath(value) {
  if (!value) return "";
  if (typeof value === "string") return normalizeProgressPath(value);
  return normalizeProgressPath(value.path);
}

function uniquePaths(values) {
  const seen = new Set();
  const paths = [];
  for (const value of values || []) {
    const path = toPath(value);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    paths.push(path);
  }
  return paths;
}

function plannedAnalyzePaths(plannedFiles = []) {
  return uniquePaths(
    (plannedFiles || []).filter((file) => {
      if (!file) return false;
      if (typeof file === "string") return true;
      return file.analyze !== false;
    })
  );
}

/**
 * Derive how close an analysis is to finishing from processed paths and the
 * planned / structure file list.
 */
export function buildAnalysisProgress({
  processedFiles = [],
  processableFiles = 0,
  plannedFiles = [],
  relatedFiles = [],
  structureFiles = [],
} = {}) {
  const processed = uniquePaths(processedFiles);
  const processedSet = new Set(processed);

  let planned = plannedAnalyzePaths(plannedFiles);
  if (planned.length === 0) {
    planned = uniquePaths(relatedFiles);
  }
  const structurePaths = uniquePaths(structureFiles);
  if (
    planned.length === 0 &&
    structurePaths.length > 0 &&
    (Number(processableFiles) <= 0 ||
      structurePaths.length === Number(processableFiles) ||
      structurePaths.length <= Number(processableFiles) + 2)
  ) {
    planned = structurePaths;
  }

  const remaining = planned.filter((path) => !processedSet.has(path));
  const done = planned.filter((path) => processedSet.has(path));
  const total = Number(processableFiles) || planned.length || processed.length;
  const processedCount = processed.length;
  const remainingCount = Math.max(0, total - processedCount);
  const percent = total > 0 ? Math.min(100, Math.floor((processedCount / total) * 100)) : 0;

  return {
    processedCount,
    remainingCount,
    total,
    percent,
    remaining,
    done,
    remainingPreview: remaining.slice(0, DISPLAY_REMAINING_LIMIT),
    remainingOverflow: Math.max(0, remaining.length - DISPLAY_REMAINING_LIMIT),
    donePreview: done.slice(-DISPLAY_DONE_LIMIT),
    doneOverflow: Math.max(0, done.length - DISPLAY_DONE_LIMIT),
    hasFileList: remaining.length > 0 || done.length > 0,
  };
}

export function filesFinished(progress) {
  return Boolean(progress && progress.total > 0 && progress.remainingCount <= 0);
}

/**
 * Coarse analysis stage. File progress hitting 100% is not the same as the
 * analysis finishing — finalize still builds the repository map.
 */
export function deriveAnalysisStage({
  status,
  finalizeStatus,
  progress,
} = {}) {
  if (status === "failed" || finalizeStatus === "failed") return "failed";
  if (status === "completed") return "completed";
  if (status === "pending" || status === "queued") return "queued";
  const finalizeRunning =
    finalizeStatus === "queued" || finalizeStatus === "running";
  if (status === "processing" || finalizeRunning) {
    if (finalizeRunning || filesFinished(progress)) return "finalizing";
    return "analyzing_files";
  }
  if (filesFinished(progress)) return "finalizing";
  return "waiting";
}

export function formatAnalysisStageLabel(stage) {
  if (stage === "queued") return "Queued";
  if (stage === "analyzing_files") return "Analyzing files";
  if (stage === "finalizing") return "Finalizing";
  if (stage === "completed") return "Ready";
  if (stage === "failed") return "Failed";
  if (stage === "waiting") return "Waiting";
  return "Analyzing files";
}

export function formatAnalysisProgressCopy(progress, { stage } = {}) {
  if (stage === "completed") {
    if (!progress || progress.total <= 0) return "Analysis complete.";
    return `Analysis complete. ${progress.processedCount} files were analyzed.`;
  }
  if (!progress || progress.total <= 0) {
    return stage === "queued"
      ? "Queued. Waiting for the file list…"
      : "Waiting for the file list…";
  }
  if (stage === "finalizing" || progress.remainingCount <= 0) {
    return `All ${progress.total} files analyzed. Building the repository map…`;
  }
  return `${progress.processedCount} of ${progress.total} files analyzed (${progress.percent}%). ${progress.remainingCount} remaining.`;
}

export function formatProgressEtaCopy(ms, { stage } = {}) {
  if (stage === "completed") return "Analysis complete";
  if (stage === "finalizing") return "File analysis finished. Finalizing…";
  if (ms === null || ms === undefined) return "Calculating…";
  if (ms <= 0) return "File analysis finished. Finalizing…";

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s remaining`;
  }
  return `${seconds}s remaining`;
}

export function analysisProgressFromRepo(repo) {
  return buildAnalysisProgress({
    processedFiles: repo?.processedFiles,
    processableFiles: repo?.processableFiles,
    plannedFiles: repo?.prReusePlan,
    relatedFiles: repo?.incrementalAnalysis?.relatedFiles,
    structureFiles: repo?.structure?.files,
  });
}

export function analysisHealthCopy(repo) {
  if (!repo) return null;
  if (repo.status === "failed" || repo.finalizeStatus === "failed") {
    const refunded = Number(repo.creditsRefunded) || 0;
    const refundNote =
      refunded > 0
        ? ` ${refunded} credit${refunded === 1 ? "" : "s"} were returned.`
        : repo.creditsRefundedAt
          ? " No credits were kept for this failed run."
          : "";
    return `${repo.finalizeError || "Analysis failed."}${refundNote}`;
  }
  const failed = repo.analysisCoverage?.failedFileCount ?? 0;
  if (failed > 0) {
    return `${failed} file${failed === 1 ? "" : "s"} failed.`;
  }
  return null;
}
